import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { FacilityProvider } from './contexts/FacilityContext'
import { PlatformAuthProvider } from './contexts/PlatformAuthContext'
import { PortalAuthProvider } from './contexts/PortalAuthContext'
import AppLayout from './components/layout/AppLayout'
import AdminLayout from './components/layout/AdminLayout'
import ComingSoon from './pages/ComingSoon'

// Auth pages (operator)
import Login           from './pages/auth/Login'
import Register        from './pages/auth/Register'
import ForgotPassword  from './pages/auth/ForgotPassword'
import ResetPassword   from './pages/auth/ResetPassword'
import VerifyOtp     from './pages/auth/VerifyOtp'
import AcceptInvite  from './pages/auth/AcceptInvite'

// Platform admin - lazy. Super-admins are a tiny fraction of traffic;
// no reason to ship 15 admin pages to every visitor.
const PlatformLogin     = lazy(() => import('./pages/platform/PlatformLogin'))
const PlatformDashboard = lazy(() => import('./pages/platform/Dashboard'))
const PlatformUsers     = lazy(() => import('./pages/platform/Users'))
const PlatformRoles     = lazy(() => import('./pages/platform/Roles'))
const PlatformAudit     = lazy(() => import('./pages/platform/AuditLog'))
const PlatformFeatureFlags = lazy(() => import('./pages/platform/FeatureFlags'))
const PlatformMaintenance  = lazy(() => import('./pages/platform/Maintenance'))
const PlatformAnnouncements = lazy(() => import('./pages/platform/Announcements'))
const PlatformSubscriptions = lazy(() => import('./pages/platform/Subscriptions'))
const PlatformEmailTemplates = lazy(() => import('./pages/platform/EmailTemplates'))
const PlatformDeliverability = lazy(() => import('./pages/platform/Deliverability'))
const PlatformAiUsage     = lazy(() => import('./pages/platform/AiUsage'))
const PlatformIntegrations = lazy(() => import('./pages/platform/Integrations'))
const PlatformExports      = lazy(() => import('./pages/platform/Exports'))
const PlatformHealth       = lazy(() => import('./pages/platform/Health'))
const PlatformSettings     = lazy(() => import('./pages/platform/Settings'))

// Operator app pages - lazy. Dashboard stays first-fetch (route hint
// via <Suspense fallback>) but hydrates from cache on subsequent nav.
const Dashboard      = lazy(() => import('./pages/Dashboard'))
const FacilityList   = lazy(() => import('./pages/facilities/FacilityList'))
const UnitMap        = lazy(() => import('./pages/units/UnitMap'))
const UnitList       = lazy(() => import('./pages/units/UnitList'))
const UnitTypeList   = lazy(() => import('./pages/units/UnitTypeList'))
const TenantList     = lazy(() => import('./pages/tenants/TenantList'))
const TenantDetail   = lazy(() => import('./pages/tenants/TenantDetail'))
const RentalList     = lazy(() => import('./pages/rentals/RentalList'))
const InvoiceList    = lazy(() => import('./pages/invoices/InvoiceList'))
const InvoiceDetail  = lazy(() => import('./pages/invoices/InvoiceDetail'))
const PaymentList    = lazy(() => import('./pages/payments/PaymentList'))
const AccessCodeList = lazy(() => import('./pages/access/AccessCodeList'))
const Activity       = lazy(() => import('./pages/Activity'))
const MoveOutRequests = lazy(() => import('./pages/MoveOutRequests'))
const Reports        = lazy(() => import('./pages/Reports'))
const Auctions       = lazy(() => import('./pages/Auctions'))
const AccountingIntegrations = lazy(() => import('./pages/AccountingIntegrations'))
const Settings       = lazy(() => import('./pages/Settings'))
const OperatorPlatformSettings = lazy(() => import('./pages/admin/PlatformSettings'))

// Marketing scope - lazy. Visitors hitting / shouldn't pull the
// operator bundle. Same for booking + portal.
const MarketingLayout    = lazy(() => import('./components/layout/MarketingLayout'))
const MarketingHome      = lazy(() => import('./pages/marketing/MarketingHome'))
const MarketingFeatures  = lazy(() => import('./pages/marketing/MarketingFeatures'))
const MarketingPricing   = lazy(() => import('./pages/marketing/MarketingPricing'))
const MarketingAbout     = lazy(() => import('./pages/marketing/MarketingAbout'))
const MarketingContact   = lazy(() => import('./pages/marketing/MarketingContact'))

// Legal - lives inside MarketingLayout so the dark chrome + footer wrap
// them. Lazy so operators signing in don't pay for the bundle.
const LegalTerms         = lazy(() => import('./pages/legal/Terms'))
const LegalPrivacy       = lazy(() => import('./pages/legal/Privacy'))
const LegalCookies       = lazy(() => import('./pages/legal/Cookies'))
const LegalAcceptableUse = lazy(() => import('./pages/legal/AcceptableUse'))

// Booking scope - lazy
const BookingLayout       = lazy(() => import('./components/layout/BookingLayout'))
const BookingFacility     = lazy(() => import('./pages/booking/BookingFacility'))
const BookingUnitType     = lazy(() => import('./pages/booking/BookingUnitType'))
const BookingCheckout     = lazy(() => import('./pages/booking/BookingCheckout'))
const BookingConfirmation = lazy(() => import('./pages/booking/BookingConfirmation'))

// Tenant portal scope - lazy
const PortalLayout         = lazy(() => import('./components/layout/PortalLayout'))
const PortalLogin          = lazy(() => import('./pages/portal/PortalLogin'))
const PortalActivate       = lazy(() => import('./pages/portal/PortalActivate'))
const PortalForgotPassword = lazy(() => import('./pages/portal/PortalForgotPassword'))
const PortalResetPassword  = lazy(() => import('./pages/portal/PortalResetPassword'))
const PortalDashboard      = lazy(() => import('./pages/portal/PortalDashboard'))
const PortalInvoices       = lazy(() => import('./pages/portal/PortalInvoices'))
const PortalInvoiceDetail  = lazy(() => import('./pages/portal/PortalInvoiceDetail'))
const PortalPaymentMethod  = lazy(() => import('./pages/portal/PortalPaymentMethod'))
const PortalAccessCodes    = lazy(() => import('./pages/portal/PortalAccessCodes'))
const PortalMoveOut        = lazy(() => import('./pages/portal/PortalMoveOut'))
const PortalProfile        = lazy(() => import('./pages/portal/PortalProfile'))

function PrivateRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
  return user ? children : <Navigate to="/login" replace />
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return null
  return user ? <Navigate to="/dashboard" replace /> : children
}

function ScopeFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

export default function App() {
  // Pre-launch gate: when prod is shipped with VITE_COMING_SOON=true the
  // entire public surface renders as the holding page. /admin/* stays
  // reachable so platform admins can still log in and flip settings
  // (including the flag itself, via redeploy) before going truly live.
  if (
    import.meta.env.VITE_COMING_SOON === 'true' &&
    !window.location.pathname.startsWith('/admin')
  ) {
    return <ComingSoon />
  }

  return (
    <BrowserRouter>
      <PlatformAuthProvider>
        <PortalAuthProvider>
          <AuthProvider>
            <FacilityProvider>
              <Suspense fallback={<ScopeFallback />}>
                <Routes>
                  {/* ── Marketing (public, lazy) ─────────────── */}
                  <Route element={<MarketingLayout />}>
                    <Route path="/"          element={<MarketingHome />} />
                    <Route path="/features"  element={<MarketingFeatures />} />
                    <Route path="/pricing"   element={<MarketingPricing />} />
                    <Route path="/about"     element={<MarketingAbout />} />
                    <Route path="/contact"   element={<MarketingContact />} />
                    <Route path="/terms"            element={<LegalTerms />} />
                    <Route path="/privacy"          element={<LegalPrivacy />} />
                    <Route path="/cookies"          element={<LegalCookies />} />
                    <Route path="/acceptable-use"   element={<LegalAcceptableUse />} />
                  </Route>

                  {/* ── Booking (public, lazy) ──────────────── */}
                  <Route path="/book/:slug" element={<BookingLayout />}>
                    <Route index                 element={<BookingFacility />} />
                    <Route path="checkout"       element={<BookingCheckout />} />
                    <Route path="confirmation"   element={<BookingConfirmation />} />
                    <Route path=":unitTypeId"    element={<BookingUnitType />} />
                  </Route>

                  {/* ── Tenant portal - public auth surface ── */}
                  <Route path="/portal/login"                   element={<PortalLogin />} />
                  <Route path="/portal/activate/:token"         element={<PortalActivate />} />
                  <Route path="/portal/forgot-password"         element={<PortalForgotPassword />} />
                  <Route path="/portal/reset-password/:token"   element={<PortalResetPassword />} />

                  {/* ── Tenant portal - authenticated ──────── */}
                  <Route path="/portal" element={<PortalLayout />}>
                    <Route index               element={<Navigate to="/portal/dashboard" replace />} />
                    <Route path="dashboard"    element={<PortalDashboard />} />
                    <Route path="invoices"     element={<PortalInvoices />} />
                    <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
                    <Route path="payment-method" element={<PortalPaymentMethod />} />
                    <Route path="access-codes" element={<PortalAccessCodes />} />
                    <Route path="move-out"     element={<PortalMoveOut />} />
                    <Route path="profile"      element={<PortalProfile />} />
                  </Route>

                  {/* ── Operator public auth ────────────────── */}
                  <Route path="/login"    element={<PublicRoute><Login /></PublicRoute>} />
                  <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                  <Route path="/forgot-password"          element={<ForgotPassword />} />
                  <Route path="/reset-password/:token"    element={<ResetPassword />} />
                  {/* /verify-otp is reachable mid-signup AND mid-login,
                      so it cannot live behind <PublicRoute>: a freshly
                      signed-up user has no token yet but a returning
                      unverified user has no user state either. Either way
                      we want them on this page. */}
                  <Route path="/verify-otp" element={<VerifyOtp />} />
                  <Route path="/accept-invite/:token" element={<AcceptInvite />} />

                  {/* ── Platform Admin SPA ──────────────────── */}
                  <Route path="/admin/login" element={<PlatformLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index                element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="dashboard"     element={<PlatformDashboard />} />
                    <Route path="users"         element={<PlatformUsers />} />
                    <Route path="roles"         element={<PlatformRoles />} />
                    <Route path="audit"         element={<PlatformAudit />} />
                    <Route path="feature-flags" element={<PlatformFeatureFlags />} />
                    <Route path="maintenance"   element={<PlatformMaintenance />} />
                    <Route path="announcements" element={<PlatformAnnouncements />} />
                    <Route path="subscriptions" element={<PlatformSubscriptions />} />
                    <Route path="email-templates" element={<PlatformEmailTemplates />} />
                    <Route path="deliverability"  element={<PlatformDeliverability />} />
                    <Route path="ai-usage"        element={<PlatformAiUsage />} />
                    <Route path="integrations"    element={<PlatformIntegrations />} />
                    <Route path="exports"         element={<PlatformExports />} />
                    <Route path="health"          element={<PlatformHealth />} />
                    <Route path="settings"        element={<PlatformSettings />} />
                  </Route>

                  {/* ── Operator app (auth required) ────────── */}
                  <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
                    <Route path="/dashboard"            element={<Dashboard />} />
                    <Route path="/facilities"           element={<FacilityList />} />
                    <Route path="/units"                element={<UnitList />} />
                    <Route path="/units/map"            element={<UnitMap />} />
                    <Route path="/units/types"          element={<UnitTypeList />} />
                    <Route path="/tenants"              element={<TenantList />} />
                    <Route path="/tenants/:id"          element={<TenantDetail />} />
                    <Route path="/rentals"              element={<RentalList />} />
                    <Route path="/rentals/new"          element={<Navigate to="/rentals" replace />} />
                    <Route path="/invoices"             element={<InvoiceList />} />
                    <Route path="/invoices/:id"         element={<InvoiceDetail />} />
                    <Route path="/payments"             element={<PaymentList />} />
                    <Route path="/access-codes"         element={<AccessCodeList />} />
                    <Route path="/move-out-requests"    element={<MoveOutRequests />} />
                    <Route path="/reports"              element={<Reports />} />
                    <Route path="/auctions"             element={<Auctions />} />
                    <Route path="/integrations/accounting" element={<AccountingIntegrations />} />
                    <Route path="/activity"             element={<Activity />} />
                    <Route path="/settings"             element={<Settings />} />
                    <Route path="/admin/platform-settings" element={<OperatorPlatformSettings />} />
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </FacilityProvider>
          </AuthProvider>
        </PortalAuthProvider>
      </PlatformAuthProvider>
    </BrowserRouter>
  )
}
